import type { RefObject, SetStateAction } from "react";
import { memo, useCallback, useContext, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { debounce } from "lodash-es";

import type {
  FormBuilderField,
  FormDraft,
  FormPartTemplate,
  MhFormContext,
  MultipleCopiesFormBuilderField,
} from "@thalamos/aspire-common";

import type { FieldProps } from "../FieldProps.js";
import { useScrollFirstErrorIntoViewEffect } from "../helpers/UseScrollFirstErrorIntoViewEffect/UseScrollFirstErrorIntoViewEffect.js";
import { fieldDependencies } from "./fieldDependencies.js";
import { safeCast } from "./helpers/safeCast.js";
import { OpenPatientEditDialog } from "./OpenPatientEditDialog.js";
import { api } from "~/api.js";
import {
  Banner,
  formBannerTypeToBannerList,
  FormTitle,
  HelperText,
  ReadOnlyContent,
} from "~/components/design-system/index.js";
import { TortusAvtLaunchBar } from "~/components/design-system/TortusAvtModal/index.js";
import {
  AmhpLocalAuthorityFormField,
  StandardLocalAuthorityFormField,
} from "~/components/form/AmhpLocalAuthorityFormField.js";
import { ApprovedClinicianConfirmationCheckboxFormField } from "~/components/form/ApprovedClinicianConfirmationCheckboxFormField.js";
import { BranchFormField } from "~/components/form/BranchFormField.js";
import { ConfirmationCheckboxFormField } from "~/components/form/ConfirmationCheckboxFormField.js";
import { DateFormField } from "~/components/form/DateFormField.js";
import { DateTimeFormField } from "~/components/form/DateTimeFormField.js";
import { DropdownFormField } from "~/components/form/DropdownFormField.js";
import { HospitalPicker } from "~/components/form/HospitalPicker.js";
import { NameAddressFormField } from "~/components/form/index.js";
import { MultipleCopiesFormField } from "~/components/form/MultipleCopiesFormField.js";
import { MultiSelectFormField } from "~/components/form/MultiSelectFormField.js";
import { PlaceOfSafetyPicker } from "~/components/form/PlaceOfSafetyPicker.js";
import { PractitionerFormField } from "~/components/form/PractitionerFormField.js";
import { Section12ConfirmationCheckboxFormField } from "~/components/form/Section12ConfirmationCheckboxFormField.js";
import { TextboxFormField } from "~/components/form/TextboxFormField.js";
import { TimeFormField } from "~/components/form/TimeFormField.js";
import type { AutoSaveAction } from "~/components/types/form.js";
import { LoggedInUserContext } from "~/Contexts.js";

export function renderField<Data extends Record<string, unknown>>(
  field: FormBuilderField<Data>,
  fieldProps: FieldProps<Data>,
) {
  switch (field.type) {
    case "separator":
      return (
        <Box
          sx={{
            mt: "2rem",
            mb: "2rem",
            width: "100%",
            height: 2,
            backgroundColor: "gray",
          }}
        />
      );
    case "label":
      return field.label.map((label) => (
        <Box sx={{ mt: 1, mb: 3 }}>
          {field.bannerType ? (
            <Banner
              title={label}
              bannerType={formBannerTypeToBannerList[field.bannerType]}
            />
          ) : field.labelType === "subtitle" ? (
            <FormTitle subtitleText={label} showBorder={true} />
          ) : (
            <>
              {field.showStaticLabel ? (
                <Box>
                  <Typography
                    component={field.showAsBulletPoint ? "ul" : "p"}
                    sx={{
                      fontSize: (theme) => theme.spacing(1.85),
                      color: (theme) => theme.palette.common.black,
                      ml: field.indent ? field.indent : 0,
                      fontWeight: "bold",
                    }}
                  >
                    {field.showAsBulletPoint ? <li>{label}</li> : label}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <FormTitle
                    useReducedTopPadding
                    hasContainerMarginBottom={false}
                    hasTitleBottomMargin={false}
                    titleText={label}
                    showBorder={true}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      ));
    case "readonly":
      return (
        <>
          <Box sx={{ mb: 3 }}>
            <ReadOnlyContent
              label={field.label}
              isActive={true}
              content={field.textFn(
                fieldProps.values,
                fieldProps.context as MhFormContext,
              )}
            />
            {field.afterLabel && <HelperText subtext={field.afterLabel} />}
            {field.isReadOnlyPatientInfo && (
              <OpenPatientEditDialog
                canGuestUserEdit={field.canGuestUserEdit}
              />
            )}
          </Box>
        </>
      );

    case "hospital-picker":
      return <HospitalPicker field={field} fieldProps={fieldProps} />;

    case "place-of-safety-picker":
      return <PlaceOfSafetyPicker field={field} fieldProps={fieldProps} />;

    case "multiple-copies":
      return (
        <MultipleCopiesFormField
          field={field as MultipleCopiesFormBuilderField<Data, any>}
          fieldProps={fieldProps}
          renderField={renderField}
        />
      );

    case "practitioner":
      return <PractitionerFormField field={field} fieldProps={fieldProps} />;

    case "name-address":
      return <NameAddressFormField field={field} fieldProps={fieldProps} />;

    case "local-authority-picker":
      return (
        <StandardLocalAuthorityFormField
          fieldProps={fieldProps}
          field={field}
        />
      );

    case "amph-local-authority-picker":
      return (
        <AmhpLocalAuthorityFormField field={field} fieldProps={fieldProps} />
      );

    case "textbox":
      return <TextboxFormField field={field} fieldProps={fieldProps} />;

    case "multi-select":
      return <MultiSelectFormField field={field} fieldProps={fieldProps} />;

    case "confirmation-checkbox":
      return (
        <ConfirmationCheckboxFormField field={field} fieldProps={fieldProps} />
      );

    case "section-12-confirmation-checkbox":
      return (
        <Section12ConfirmationCheckboxFormField
          field={field}
          fieldProps={fieldProps}
        />
      );

    case "approved-clinician-confirmation-checkbox":
      return (
        <ApprovedClinicianConfirmationCheckboxFormField
          field={field}
          fieldProps={fieldProps}
        />
      );

    case "branch":
      return (
        <BranchFormField
          field={field}
          fieldProps={fieldProps}
          renderField={renderField}
        />
      );

    case "date-time":
      return <DateTimeFormField field={field} fieldProps={fieldProps} />;

    case "dropdown":
      return <DropdownFormField field={field} fieldProps={fieldProps} />;

    case "date":
      return <DateFormField field={field} fieldProps={fieldProps} />;

    case "time":
      return <TimeFormField field={field} fieldProps={fieldProps} />;
  }
}

/** Collect `{ field: label }` for top-level fields, for the AVT review screen. */
function buildFieldLabels<Data extends Record<string, unknown>>(
  fields: FormBuilderField<Data>[],
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const field of fields) {
    if (
      "field" in field &&
      typeof field.field === "string" &&
      "label" in field &&
      typeof field.label === "string"
    ) {
      labels[field.field] = field.label;
    }
  }
  return labels;
}

export function ThalamosForm({
  formDraft,
  fieldProps,
  partTemplate,
  dispatchAutoSaveState,
  submitCount,
  isSubmitting,
  isAutoSaving,
  redirectToFormPage,
}: {
  partTemplate: FormPartTemplate<any>;
  fieldProps: FieldProps<any>;
  formDraft: FormDraft;
  dispatchAutoSaveState: (state: AutoSaveAction) => void;
  submitCount: number;
  isSubmitting: boolean;
  isAutoSaving: boolean;
  redirectToFormPage: () => void;
}) {
  // Debounced auto-save action
  const autoSave = useCallback(
    debounce(async (fieldProps: FieldProps<any>) => {
      try {
        const result = await api.drafts.updateData(
          formDraft.id,
          safeCast(fieldProps.validationSchema, fieldProps.values),
        );

        if (result.status === 204) {
          dispatchAutoSaveState("success");
        } else if (result.status === 404) {
          redirectToFormPage();
        } else {
          dispatchAutoSaveState("error");
        }
      } catch {
        // TODO: log error to datadog?
        dispatchAutoSaveState("error");
      }
    }, 1000),
    [formDraft.id, dispatchAutoSaveState],
  );

  // Trigger auto-save on field change
  useEffect(() => {
    if (!isAutoSaving) dispatchAutoSaveState("loading");
    autoSave(fieldProps);
  }, [autoSave, fieldProps.values]);

  // A skipped field keeps the values it last rendered with, so the writes it
  // makes are reconciled against these rather than against its own snapshot.
  // Assigned during render, not from an effect: effects run child-first, so a
  // field that writes a value as it mounts would be reconciled against the
  // values from before the change that mounted it, undoing that change.
  const latestValues = useRef<DraftData>(fieldProps.values as DraftData);
  latestValues.current = fieldProps.values as DraftData;

  // Auto-scroll to the first form error on submit
  useScrollFirstErrorIntoViewEffect([submitCount, isSubmitting]);

  // AVT (ambient voice) launcher — only on forms that declare a section
  // mapping, and only for orgs with the relevant flag(s) enabled.
  const orgConfig =
    useContext(LoggedInUserContext)?.user?.sessionOrganisationConfiguration;
  const avtSections = partTemplate.avtSections;
  const avtRealEnabled = !!orgConfig?.tortusAvtDictationEnabled;
  const avtFakeEnabled = !!orgConfig?.tortusAvtFakeDictationEnabled;
  const mhContext = fieldProps.context as MhFormContext | undefined;

  return (
    <>
      {avtSections && (avtRealEnabled || avtFakeEnabled) && (
        <TortusAvtLaunchBar
          sections={avtSections}
          fieldLabels={buildFieldLabels(partTemplate.formBuilderFields)}
          currentValues={fieldProps.values as Record<string, string>}
          realEnabled={avtRealEnabled}
          fakeEnabled={avtFakeEnabled}
          patient={mhContext?.patient}
          formDraftId={formDraft.id}
          onApply={(updates) => {
            fieldProps.setValues({ ...fieldProps.values, ...updates });
          }}
        />
      )}
      {partTemplate.formBuilderFields.map((field, index) => (
        <MemoisedField
          key={fieldKey(field, index)}
          field={field}
          fieldProps={fieldProps}
          latestValues={latestValues}
        />
      ))}
    </>
  );
}

/** The shape the form builder works in, as `RenderField` declares it. */
type DraftData = Record<string, unknown>;

/**
 * Position alone is unique; the type and key are included so that a field
 * replaced at the same position remounts rather than reusing the previous
 * one's state.
 */
function fieldKey<Data extends Record<string, unknown>>(
  field: FormBuilderField<Data>,
  index: number,
): string {
  const named = field as unknown as { field?: string };
  return `${String(index)}:${field.type}:${named.field ?? ""}`;
}

/** Shallow: a context whose nested objects are replaced without a top-level
 * key changing will not re-render the field. */
function sameContext(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    a === null ||
    b === null ||
    typeof a !== "object" ||
    typeof b !== "object"
  ) {
    return false;
  }
  const aEntries = Object.entries(a);
  const bRecord = b as Record<string, unknown>;
  return (
    aEntries.length === Object.keys(bRecord).length &&
    aEntries.every(([key, value]) => value === bRecord[key])
  );
}

/**
 * Every field writes by spreading the whole form's values and replacing its own
 * keys, so a field that did not re-render would write back a snapshot missing
 * every edit made elsewhere since. Taking only the keys whose values the field
 * actually changed, and applying them to the latest, makes that impossible.
 *
 * Sound only while the keys a field writes are among the keys `fieldDependencies`
 * reports it reads: a field is skipped only while the keys it reads are
 * unchanged, so a key it means to change cannot have moved underneath it.
 */
function reconcile(
  latest: Record<string, unknown>,
  rendered: Record<string, unknown>,
  intended: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...latest };
  for (const [key, value] of Object.entries(intended)) {
    if (value !== rendered[key]) merged[key] = value;
  }
  return merged;
}

/** Renders one field. Wrapped as {@link MemoisedField}, which owns the skipping. */
function MemoisedFieldInner<Data extends DraftData>({
  field,
  fieldProps,
  latestValues,
}: {
  field: FormBuilderField<Data>;
  fieldProps: FieldProps<Data>;
  latestValues: RefObject<Data>;
}) {
  const rendered = fieldProps.values;
  const underlying = fieldProps.setValues;

  const setValues = useCallback(
    (next: SetStateAction<Data>) => {
      const latest = latestValues.current;
      if (typeof next === "function") {
        underlying(next(latest));
        return;
      }
      underlying(reconcile(latest, rendered, next) as Data);
    },
    [rendered, underlying, latestValues],
  );

  return <>{renderField(field, { ...fieldProps, setValues })}</>;
}

/**
 * Renders one field, skipping the work when nothing it reads has changed.
 *
 * Every field is re-rendered on every keystroke otherwise, because the form's
 * values are one object that Formik replaces on each change. What a field
 * reads comes from {@link fieldDependencies}, which answers `"all"` whenever it
 * cannot bound the answer — so a field is only skipped on evidence, never by
 * default.
 *
 * `memo` erases the generic, and the cast puts it back so that a caller's field
 * and its form data still have to agree.
 */
const MemoisedField = memo(MemoisedFieldInner, (previous, next) => {
  const keys = fieldDependencies(previous.field);
  if (keys === "all" || previous.field !== next.field) return false;

  const before = previous.fieldProps;
  const after = next.fieldProps;
  for (const key of keys) {
    if (
      before.values[key] !== after.values[key] ||
      before.errors[key] !== after.errors[key] ||
      before.touched[key] !== after.touched[key]
    ) {
      return false;
    }
  }

  // The rest of what a field is handed, compared by identity: a caller that
  // rebuilds any of these each render defeats the skip entirely.
  return (
    before.validationSchema === after.validationSchema &&
    before.setValues === after.setValues &&
    before.handleBlur === after.handleBlur &&
    before.setFieldTouched === after.setFieldTouched &&
    before.formPrefixKey === after.formPrefixKey &&
    sameContext(before.context, after.context)
  );
}) as typeof MemoisedFieldInner;
